// Locations a coordinator created for Production Activities, offered again in
// the same view. Each lives on the activities that use it (Production
// Activities: Location Name / Address, with the activity's Time Zone) — it is
// not added to the EPS or the Proposal App.

const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

/** A stable option value for a created location: "saved:<name>|<address>". */
export function savedLocationRef(loc) {
  return `saved:${norm(loc && loc.name)}|${norm(loc && loc.address)}`;
}

/** Created locations among a view's activity records, once each, with the time zone they were saved with. */
export function savedLocations(records) {
  const out = [];
  const seen = {};
  (records || []).forEach((r) => {
    const loc = r.location || {};
    if (loc.type !== 'custom' || !loc.name) return;
    const ref = savedLocationRef(loc);
    if (seen[ref]) {
      if (!seen[ref].timeZone && r.timeZone) seen[ref].timeZone = r.timeZone;
      return;
    }
    seen[ref] = { ref, name: loc.name, address: loc.address || '', timeZone: r.timeZone || '' };
    out.push(seen[ref]);
  });
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

// States wholly (or almost wholly) in one zone; a split state is decided by the place's UTC offset.
const STATE_ZONE = {
  CT: 'ET', DE: 'ET', DC: 'ET', GA: 'ET', ME: 'ET', MD: 'ET', MA: 'ET', NH: 'ET', NJ: 'ET', NY: 'ET',
  NC: 'ET', OH: 'ET', PA: 'ET', RI: 'ET', SC: 'ET', VT: 'ET', VA: 'ET', WV: 'ET',
  AL: 'CT', AR: 'CT', IL: 'CT', IA: 'CT', LA: 'CT', MN: 'CT', MS: 'CT', MO: 'CT', OK: 'CT', WI: 'CT',
  AZ: 'MT', CO: 'MT', MT: 'MT', NM: 'MT', UT: 'MT', WY: 'MT',
  CA: 'PT', NV: 'PT', WA: 'PT'
};

/** Whether US daylight time is in effect on `when` (second Sunday of March → first Sunday of November). */
function usDaylight(when) {
  const y = when.getUTCFullYear();
  const sunday = (month, nth) => {
    const d = new Date(Date.UTC(y, month, 1));
    return 1 + ((7 - d.getUTCDay()) % 7) + (nth - 1) * 7;
  };
  const start = Date.UTC(y, 2, sunday(2, 2), 7);
  const end = Date.UTC(y, 10, sunday(10, 1), 6);
  return when.getTime() >= start && when.getTime() < end;
}

/**
 * A Google place → 'ET' | 'CT' | 'MT' | 'PT' | '' (unknown: the coordinator chooses).
 * The state decides when it lies in one zone; otherwise the place's current UTC offset does.
 */
export function zoneForPlace(place, when = new Date()) {
  const comps = (place && place.address_components) || [];
  const state = (comps.find((c) => (c.types || []).indexOf('administrative_area_level_1') !== -1) || {}).short_name || '';
  if (STATE_ZONE[state]) return STATE_ZONE[state];
  const offset = place && typeof place.utc_offset_minutes === 'number' ? place.utc_offset_minutes : null;
  if (offset === null) return '';
  const hours = offset / 60 + (usDaylight(when) ? -1 : 0);
  return { '-5': 'ET', '-6': 'CT', '-7': 'MT', '-8': 'PT' }[String(hours)] || '';
}
